import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Factory } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { fmt, tooltipStyle } from "./oversiktConstants";
import { dec, num } from "@/lib/formatters";

const growth = (a, b) => (a ? Math.round(((b - a) / a) * 1000) / 10 : null);
const cls = (v) => (v > 0 ? "text-[#2F7D5B]" : v < 0 ? "text-[#C0392B]" : "text-[#8AA4BF]");
const signed = (v, suffix = "") => (v === null || v === undefined ? "—" : `${v > 0 ? "+" : ""}${typeof v === "number" && suffix ? dec(v, 1) : fmt(v)}${suffix}`);

const NaeringCompareChart = ({ pendlingData, colors }) => {
  const kommuner = Object.values(pendlingData || {}).filter((k) => k.naering_history?.length >= 2);
  if (kommuner.length === 0) return null;
  const start = Math.max(...kommuner.map((k) => k.naering_history[0].year));
  const end = Math.min(...kommuner.map((k) => k.naering_history[k.naering_history.length - 1].year));
  if (end <= start) return null;
  const labels = kommuner[0].nace_labels || {};
  const at = (k, year) => k.naering_history.find((h) => h.year === year)?.arbeidssted || {};
  const sumAll = (obj) => Object.entries(obj).filter(([c]) => c !== "00").reduce((s, [, v]) => s + v, 0);
  const rows = [{ code: "ALL", name: "Alle næringar" }, ...Object.keys(labels).filter((c) => c !== "00").map((c) => ({ code: c, name: labels[c] }))].map((r) => {
    const per = kommuner.map((k) => {
      const a = r.code === "ALL" ? sumAll(at(k, start)) : at(k, start)[r.code] || 0;
      const b = r.code === "ALL" ? sumAll(at(k, end)) : at(k, end)[r.code] || 0;
      return { name: k.name, first: a, last: b, diff: b - a, pct: growth(a, b) };
    });
    const valid = per.filter((p) => p.pct !== null);
    return { ...r, per, avg: valid.length ? valid.reduce((s, p) => s + p.pct, 0) / valid.length : -Infinity };
  });
  const all = rows[0];
  const sorted = rows.slice(1).sort((a, b) => b.avg - a.avg);
  const chartRows = [all, ...sorted].map((r) => ({ name: r.name, ...Object.fromEntries(r.per.map((p) => [p.name, p.pct ?? 0])) }));
  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-2" data-testid="compare-naering-chart">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Factory className="w-5 h-5 text-[#103766]" />
          Næringsutvikling {start}–{end}: vekst i arbeidsplassar per næring (%)
        </CardTitle>
        <p className="text-sm text-[#4B647F]">Arbeidsplassar i kommunen etter arbeidsstad. Næringane er sortert etter gjennomsnittleg vekst for dei valde kommunane.</p>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div className="flex flex-wrap gap-3" data-testid="compare-naering-total">
          {all.per.map((p, i) => (
            <div key={p.name} className="p-3 border border-[#E6E9F0] rounded-lg min-w-[180px]" data-testid={`compare-naering-total-${p.name}`}>
              <p className="text-xs text-[#8AA4BF] flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: colors[i % colors.length] }} />{p.name}</p>
              <p className={`text-2xl font-light ${cls(p.pct)}`}>{signed(p.pct, " %")}</p>
              <p className="text-[11px] text-[#4B647F]">{signed(p.diff)} arbeidsplassar ({fmt(p.first)} → {fmt(p.last)})</p>
            </div>
          ))}
        </div>
        <div className="h-[600px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartRows} layout="vertical" barGap={1} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis type="number" stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${num(v)} %`} />
              <YAxis type="category" dataKey="name" width={230} stroke="#8AA4BF" fontSize={12} interval={0} />
              <Tooltip formatter={(v) => `${v > 0 ? "+" : ""}${num(v)} %`} contentStyle={tooltipStyle} />
              <Legend itemSorter={null} />
              <ReferenceLine x={0} stroke="#4B647F" />
              {kommuner.map((k, i) => <Bar key={k.name} dataKey={k.name} fill={colors[i % colors.length]} radius={[0, 4, 4, 0]} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="compare-naering-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.1em] text-[#8AA4BF] border-b border-[#E6E9F0]">
                <th className="py-2 pr-3 font-semibold">Næring</th>
                {kommuner.map((k) => <th key={k.name} className="py-2 px-3 font-semibold text-right">{k.name}<br /><span className="normal-case tracking-normal font-normal">endring {start}–{end}</span></th>)}
              </tr>
            </thead>
            <tbody>
              {[all, ...sorted].map((r) => (
                <tr key={r.code} className={`border-b border-[#F1F4F8] text-[#1F2A36] ${r.code === "ALL" ? "font-medium bg-[#F8FAFC]" : ""}`} data-testid={`compare-naering-row-${r.code}`}>
                  <td className="py-1.5 pr-3">{r.name}</td>
                  {r.per.map((p) => (
                    <td key={p.name} className="py-1.5 px-3 text-right tabular-nums">
                      <span className={cls(p.diff)}>{signed(p.diff)}</span> <span className="text-xs text-[#8AA4BF]">({signed(p.pct, " %")})</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[#8AA4BF] text-center">Kjelde: SSB 12539 – sysselsette etter arbeidsstad og næring, 15–74 år, 4. kvartal. Perioden startar i det første året alle valde kommunar har tal for.</p>
      </CardContent>
    </Card>
  );
};

export default NaeringCompareChart;
